import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';

import theme from '@styles/theme';
import { playlistHeader, logHeader } from '@lib/data';
import { DID_DATA, PLAYLIST_DATA, LOG_DATA } from '@lib/DUMMY';
import { didInfoVar } from '@store/index';
import {
    getDeviceCategory,
    getDeviceType,
    getUseStatus,
    getNotationMethod,
    getSubscriptionType
} from '@lib/enum';

import {
    InfoSection,
    InfoDataRow,
    InfoWrapper,
    InfoTitle
} from '@components/share/Info/InfoSection';
import {
    Wrapper,
    FlexBox,
    ColorPalette,
    TextButton
} from '@components/styled/StyledComponents';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import TableHeader from '@components/share/Table/TableHeader';
import PlaylistTableRow from '@components/features/Equipment/PlaylistTableRow';
import LogTableRow from '@components/features/Equipment/LogTableRow';
import Headline from '@components/layout/Headline';
import GaugeGraph from '@components/share/Graph/GaugeGraph';
import DidViewer from '@components/features/Equipment/DidViewer';
import Loader from '@components/share/Loader';

const EquipmentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { version, type } = useReactiveVar(didInfoVar);
    const [take, setTake] = useState(3);
    const [loading, setLoading] = useState(false);
    const [aspect, setAspect] = useState<number>(16 / 9);

    const totalLength = PLAYLIST_DATA.length;

    // 버전, 타입 별 이미지 비율 처리
    useEffect(() => {
        if (version === 1) {
            if (type === 3) {
                setAspect(1);
            } else if (type === 4) {
                setAspect(9 / 16);
            } else {
                setAspect(16 / 9);
            }
        } else if (version === 2) {
            setAspect(9 / 16);
        } else if (version === 3) {
            setAspect(1);
        }
    }, [version, type]);

    return (
        <Wrapper>
            <Headline
                title="신형(DID) 스탠드 / 셋톱박스"
                subTitle={`설치일자 ${dayjs('2021-01-01').format(
                    'YYYY-MM-DD'
                )} / ${48}개월`}
                buttonTitle="미디어 편집"
                buttonAction={() => navigate(`/equipment/${id}/edit`)}
                TOOLTIP
            />
            <InfoSection title="기본정보">
                <FlexBox
                    $justifyContent="space-between"
                    $alignItems="flex-start"
                >
                    <InfoWrapper $width="467px">
                        <InfoDataRow
                            title="헬로 미디어 타입"
                            value={`${version || 0}.0`}
                            titleColor={theme.colors.positiveBlue}
                            fontColor={theme.colors.positiveBlue}
                            fontFamily="PretendardMedium"
                            fontSize={16}
                        />
                        <InfoDataRow
                            title="장비 구분"
                            value={(getDeviceCategory(1) as string) || '-'}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="유형"
                            value={(getDeviceType(1) as string) || '-'}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="연동장비명칭"
                            value="-"
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="환자 호출 여/부"
                            value={(getUseStatus(true) as string) || '-'}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="대기환자 사용"
                            value={(getUseStatus(true) as string) || '-'}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="대기환자 타이틀"
                            value={(getNotationMethod(1) as string) || '-'}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow title="배경색상" fontSize={16} DETAIL>
                            F3F4F6{' '}
                            <ColorPalette
                                $bgColor="#F3F4F6"
                                $margin="0 0 0 8px"
                            />
                        </InfoDataRow>
                        <InfoDataRow
                            title="대기환자 강조색상"
                            fontSize={16}
                            margin="0"
                            DETAIL
                        >
                            0078FF{' '}
                            <ColorPalette
                                $bgColor="#0078FF"
                                $margin="0 0 0 8px"
                            />
                        </InfoDataRow>
                    </InfoWrapper>
                    <InfoWrapper $width="467px">
                        <InfoDataRow
                            title="기기 타입"
                            value={(getSubscriptionType(1) as string) || '-'}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="설치 앱 버전"
                            value="Version 2.0.0"
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="차트(EMR)"
                            value="이지스차트"
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="신청일자"
                            value={dayjs('2025-06-23').format(
                                'YYYY-MM-DD HH:mm'
                            )}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="설치일"
                            value={dayjs('2025-06-23').format(
                                'YYYY-MM-DD HH:mm'
                            )}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="설치기간"
                            value={`${47}개월`}
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow
                            title="출고 창고"
                            value="포리얼 경상"
                            fontSize={16}
                            DETAIL
                        />
                        <InfoDataRow title="설치업체" value="포리얼" DETAIL />
                        <InfoDataRow
                            title="기기 위치"
                            value="원내 로비"
                            fontSize={16}
                            margin="0"
                            DETAIL
                        />
                    </InfoWrapper>
                </FlexBox>
            </InfoSection>
            <InfoSection title="금일 가동 현황" margin="0 0 20px">
                <GaugeGraph title={'6h 36m'} />
            </InfoSection>
            <InfoSection>
                <FlexBox $alignItems="flex-start" $gap={28}>
                    <InfoWrapper $width="305px">
                        <InfoTitle $margin="0 0 32px">출력 예시 화면</InfoTitle>
                        <DidViewer data={DID_DATA} />
                    </InfoWrapper>
                    <InfoWrapper $width="622px">
                        <InfoTitle $margin="0 0 32px">
                            플레이리스트 (구간1)
                        </InfoTitle>
                        <TableHeader
                            header={playlistHeader}
                            bgColor={theme.colors.ultraLightGrayBorder}
                            LAST_CELL
                        />
                        <TableBody $height={version !== 2 ? 180 : 276}>
                            {loading && <Loader />}
                            {totalLength === 0 ? (
                                <EmptyTable>
                                    플레이리스트가 존재하지 않습니다.
                                </EmptyTable>
                            ) : (
                                PLAYLIST_DATA.map((data, idx) => (
                                    <PlaylistTableRow
                                        key={`${idx}-playlist-list`}
                                        header={playlistHeader}
                                        data={data}
                                        imgAspect={aspect}
                                    />
                                ))
                            )}
                        </TableBody>
                        {version === 1 && type === 2 && (
                            <>
                                <InfoTitle $margin="32px 0">
                                    플레이리스트 (구간2)
                                </InfoTitle>
                                <TableHeader
                                    header={playlistHeader}
                                    bgColor={theme.colors.ultraLightGrayBorder}
                                    LAST_CELL
                                />
                                <TableBody $height={180}>
                                    {loading && <Loader />}
                                    {totalLength === 0 ? (
                                        <EmptyTable>
                                            플레이리스트가 존재하지 않습니다.
                                        </EmptyTable>
                                    ) : (
                                        PLAYLIST_DATA.map((data, idx) => (
                                            <PlaylistTableRow
                                                key={`${idx}-playlist-list`}
                                                header={playlistHeader}
                                                data={data}
                                                imgAspect={aspect}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </>
                        )}
                    </InfoWrapper>
                </FlexBox>
            </InfoSection>
            <InfoSection
                title="기기 로그"
                subTitle={dayjs('2025-06-13').format('YY.MM.DD HH:mm')}
            >
                <TableHeader
                    header={logHeader}
                    bgColor={theme.colors.ultraLightGrayBorder}
                    LAST_CELL
                />
                <TableBody $minHeight={150}>
                    {loading && <Loader />}
                    {totalLength === 0 ? (
                        <EmptyTable>로그가 존재하지 않습니다.</EmptyTable>
                    ) : (
                        LOG_DATA.slice(0, take).map((data, idx) => (
                            <LogTableRow
                                key={`${idx}-log-list`}
                                header={logHeader}
                                data={data}
                            />
                        ))
                    )}
                </TableBody>
                <TextButton
                    $margin="20px auto 0"
                    $fontColor={theme.colors.sidebarGrayFontColor}
                    onClick={() => setTake((prev) => prev + 3)}
                >
                    더보기
                </TextButton>
            </InfoSection>
        </Wrapper>
    );
};

export default EquipmentDetail;
